import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

interface Company {
  id: string; name: string; slug: string; industry?: string;
  users_count: number; groups_count: number; leads_count: number;
  facebook_status: string; facebook_user_id?: string;
}
interface CompanyDetail extends Company {
  users: { id: string; email: string; full_name?: string; role: string }[];
  facebook: null | { status: string; fb_user_id?: string; imported_at?: string; last_ok_at?: string; last_error?: string };
}

const api = {
  async get(path: string) { const r = await fetch(`/api/v1/admin${path}`); if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json(); },
  async post(path: string, body?: any) { const r = await fetch(`/api/v1/admin${path}`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: body ? JSON.stringify(body) : undefined }); if (!r.ok) throw new Error((await r.json()).detail || 'Error'); return r.json().catch(() => ({})); },
  async del(path: string) { const r = await fetch(`/api/v1/admin${path}`, { method: 'DELETE' }); if (!r.ok && r.status !== 204) throw new Error('Error'); },
};

function Stat({ label, value }: { label: string; value: number | string }) {
  return (
    <div className="bg-white rounded-xl border border-gray-100 p-4">
      <div className="text-2xl font-bold text-gray-900">{value}</div>
      <div className="text-xs text-gray-500 mt-0.5">{label}</div>
    </div>
  );
}

const FB_BADGE: Record<string, string> = {
  connected: 'bg-emerald-100 text-emerald-700',
  expired: 'bg-amber-100 text-amber-700',
  not_connected: 'bg-gray-100 text-gray-500',
};

export default function AdminPanel() {
  const { user, logout } = useAuth();
  const [overview, setOverview] = useState<any>(null);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [detail, setDetail] = useState<CompanyDetail | null>(null);
  const [showNewCompany, setShowNewCompany] = useState(false);
  const [error, setError] = useState('');

  const reload = async () => {
    try {
      setOverview(await api.get('/overview'));
      setCompanies((await api.get('/companies')).companies);
    } catch (e: any) { setError(e.message); }
  };
  useEffect(() => { reload(); }, []);

  const openCompany = async (id: string) => {
    try { setDetail(await api.get(`/companies/${id}`)); } catch (e: any) { setError(e.message); }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-slate-900 text-white px-6 py-3 flex items-center justify-between">
        <div className="font-semibold">Estaie · Admin Console</div>
        <div className="flex items-center gap-4 text-sm">
          <span className="text-slate-400">{user?.email}</span>
          <button onClick={logout} className="px-3 py-1 bg-slate-700 rounded-lg hover:bg-slate-600">Logout</button>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-6 space-y-6">
        {error && <div className="bg-red-50 text-red-700 text-sm p-3 rounded-lg">{error}</div>}

        {/* Overview */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <Stat label="Companies" value={overview.companies} />
            <Stat label="Users" value={overview.users} />
            <Stat label="Total leads" value={overview.leads} />
            <Stat label="Facebook connected" value={overview.facebook_connected} />
          </div>
        )}

        {/* Companies */}
        <div className="bg-white rounded-xl border border-gray-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-50">
            <h2 className="font-semibold text-gray-900">Companies</h2>
            <button onClick={() => setShowNewCompany(true)}
              className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700">+ New Company</button>
          </div>
          <table className="w-full text-sm">
            <thead className="text-left text-gray-400 text-xs uppercase">
              <tr><th className="px-5 py-2">Company</th><th className="py-2">Users</th><th className="py-2">Groups</th><th className="py-2">Leads</th><th className="py-2">Facebook</th><th></th></tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {companies.map((c) => (
                <tr key={c.id} className="hover:bg-gray-50/60">
                  <td className="px-5 py-3">
                    <div className="font-medium text-gray-800">{c.name}</div>
                    <div className="text-xs text-gray-400">{c.slug}</div>
                  </td>
                  <td className="py-3">{c.users_count}</td>
                  <td className="py-3">{c.groups_count}</td>
                  <td className="py-3">{c.leads_count}</td>
                  <td className="py-3"><span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FB_BADGE[c.facebook_status] || FB_BADGE.not_connected}`}>{c.facebook_status}</span></td>
                  <td className="py-3 pr-5 text-right">
                    <button onClick={() => openCompany(c.id)} className="text-blue-600 hover:underline text-sm">Manage</button>
                  </td>
                </tr>
              ))}
              {companies.length === 0 && <tr><td colSpan={6} className="px-5 py-8 text-center text-gray-400">No companies yet</td></tr>}
            </tbody>
          </table>
        </div>
      </main>

      {showNewCompany && <NewCompanyModal onClose={() => setShowNewCompany(false)} onDone={() => { setShowNewCompany(false); reload(); }} />}
      {detail && <CompanyDrawer detail={detail} onClose={() => setDetail(null)} onChange={() => { openCompany(detail.id); reload(); }} />}
    </div>
  );
}

function NewCompanyModal({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const [name, setName] = useState(''); const [industry, setIndustry] = useState(''); const [err, setErr] = useState('');
  const submit = async () => { try { await api.post('/companies', { name, industry: industry || null }); onDone(); } catch (e: any) { setErr(e.message); } };
  return (
    <Modal title="New Company" onClose={onClose}>
      <Field label="Company name"><input value={name} onChange={(e) => setName(e.target.value)} className={inputCls} /></Field>
      <Field label="Industry (optional)"><input value={industry} onChange={(e) => setIndustry(e.target.value)} className={inputCls} /></Field>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <button onClick={submit} disabled={name.length < 2} className={btnCls}>Create</button>
    </Modal>
  );
}

function CompanyDrawer({ detail, onClose, onChange }: { detail: CompanyDetail; onClose: () => void; onChange: () => void }) {
  const [showAddUser, setShowAddUser] = useState(false);
  const [cookieJson, setCookieJson] = useState(''); const [fbMsg, setFbMsg] = useState('');

  const importCookies = async () => {
    setFbMsg('');
    try {
      const parsed = JSON.parse(cookieJson);
      const cookies = Array.isArray(parsed) ? parsed : parsed.cookies ?? [];
      const r = await api.post(`/companies/${detail.id}/facebook/import-cookies`, { cookies });
      setFbMsg(`✅ Connected (${r.cookies} cookies)`); setCookieJson(''); onChange();
    } catch (e: any) { setFbMsg(`❌ ${e.message}`); }
  };
  const disconnect = async () => { await api.post(`/companies/${detail.id}/facebook/disconnect`); onChange(); };
  const delUser = async (id: string) => { if (confirm('Delete this user?')) { await api.del(`/users/${id}`); onChange(); } };

  return (
    <div className="fixed inset-0 bg-black/30 flex justify-end z-40" onClick={onClose}>
      <div className="w-full max-w-lg bg-gray-50 h-full overflow-y-auto p-6 space-y-5" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between">
          <div><h2 className="text-lg font-bold text-gray-900">{detail.name}</h2><p className="text-xs text-gray-400">{detail.slug}</p></div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-700 text-xl">✕</button>
        </div>

        {/* Users */}
        <section className="bg-white rounded-xl border border-gray-100 p-4">
          <div className="flex items-center justify-between mb-3">
            <h3 className="font-semibold text-gray-800 text-sm">Users ({detail.users.length})</h3>
            <button onClick={() => setShowAddUser(true)} className="text-blue-600 text-sm hover:underline">+ Add user</button>
          </div>
          <div className="space-y-2">
            {detail.users.map((u) => (
              <div key={u.id} className="flex items-center justify-between text-sm border border-gray-50 rounded-lg px-3 py-2">
                <div><span className="text-gray-800">{u.email}</span> <span className="text-xs text-gray-400 ml-1">{u.role}</span></div>
                <button onClick={() => delUser(u.id)} className="text-gray-300 hover:text-red-500">✕</button>
              </div>
            ))}
          </div>
        </section>

        {/* Facebook */}
        <section className="bg-white rounded-xl border border-gray-100 p-4">
          <h3 className="font-semibold text-gray-800 text-sm mb-2">Facebook connection</h3>
          <p className="text-sm mb-3">
            Status: <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${FB_BADGE[detail.facebook?.status || 'not_connected']}`}>{detail.facebook?.status || 'not_connected'}</span>
            {detail.facebook?.fb_user_id && <span className="text-xs text-gray-400 ml-2">FB id {detail.facebook.fb_user_id}</span>}
          </p>
          {detail.facebook?.last_error && <p className="text-xs text-red-500 mb-2">Last error: {detail.facebook.last_error}</p>}
          <p className="text-xs text-gray-500 mb-2">Paste Cookie-Editor JSON (exported from facebook.com) to connect on their behalf:</p>
          <textarea value={cookieJson} onChange={(e) => setCookieJson(e.target.value)} placeholder='[{"name":"c_user",...}]'
            className="w-full h-20 p-2 border border-gray-200 rounded-lg font-mono text-xs" />
          <div className="flex items-center gap-2 mt-2">
            <button onClick={importCookies} disabled={!cookieJson.trim()} className="px-3 py-1.5 bg-blue-600 text-white rounded-lg text-sm disabled:opacity-50">Connect / Update</button>
            {detail.facebook && <button onClick={disconnect} className="px-3 py-1.5 bg-gray-100 text-gray-600 rounded-lg text-sm hover:bg-gray-200">Disconnect</button>}
            {fbMsg && <span className="text-sm">{fbMsg}</span>}
          </div>
        </section>

        {showAddUser && <AddUserModal companyId={detail.id} onClose={() => setShowAddUser(false)} onDone={() => { setShowAddUser(false); onChange(); }} />}
      </div>
    </div>
  );
}

function AddUserModal({ companyId, onClose, onDone }: { companyId: string; onClose: () => void; onDone: () => void }) {
  const [email, setEmail] = useState(''); const [password, setPassword] = useState(''); const [role, setRole] = useState('admin'); const [err, setErr] = useState('');
  const submit = async () => { try { await api.post('/users', { company_id: companyId, email, password, role }); onDone(); } catch (e: any) { setErr(e.message); } };
  return (
    <Modal title="Add User" onClose={onClose}>
      <Field label="Email"><input value={email} onChange={(e) => setEmail(e.target.value)} className={inputCls} /></Field>
      <Field label="Password (min 8)"><input value={password} onChange={(e) => setPassword(e.target.value)} className={inputCls} /></Field>
      <Field label="Role">
        <select value={role} onChange={(e) => setRole(e.target.value)} className={inputCls}>
          <option value="admin">admin</option><option value="member">member</option>
        </select>
      </Field>
      {err && <p className="text-sm text-red-600">{err}</p>}
      <button onClick={submit} disabled={!email || password.length < 8} className={btnCls}>Create user</button>
    </Modal>
  );
}

const inputCls = 'w-full px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent';
const btnCls = 'w-full py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50';

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return <div><label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>{children}</div>;
}
function Modal({ title, children, onClose }: { title: string; children: React.ReactNode; onClose: () => void }) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm space-y-3" onClick={(e) => e.stopPropagation()}>
        <h3 className="font-bold text-gray-900">{title}</h3>
        {children}
      </div>
    </div>
  );
}
